import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
  /** User-selected theme preference. `'system'` follows OS dark/light mode. */
  theme: ThemeMode;
  /** Resolved theme actually applied to the DOM (`'system'` resolves to `light`/`dark`). */
  resolved: 'light' | 'dark';
  setTheme: (mode: ThemeMode) => void;
  /** Re-evaluate `theme` against `prefers-color-scheme` and apply the `.dark` class. */
  apply: () => void;
  /** Idempotent — call once before render to wire up persistence + media-query listener. */
  bootstrap: () => void;
}

const STORAGE_KEY = 'theme';

function readStoredTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'light';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    /* localStorage may be unavailable in private mode — fall through to default */
  }
  // LIGHT UNTIL THE READER CHOOSES OTHERWISE (owner, 2026-10-18: "make default
  // colour of the app light"). `system` is still one choice away in
  // Preferences, and a stored choice — `system` included — is kept.
  return 'light';
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return mode;
}

function applyToDom(resolved: 'light' | 'dark'): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  // Hint to the browser for built-in form controls / scrollbars.
  document.documentElement.style.colorScheme = resolved;
}

let mediaListenerAttached = false;

export const useThemeStore = create<ThemeState>()(
  subscribeWithSelector((set, get) => ({
    theme: 'light',
    resolved: 'light',
    setTheme(mode) {
      try {
        window.localStorage.setItem(STORAGE_KEY, mode);
      } catch {
        /* ignore quota / privacy errors */
      }
      const resolved = resolveTheme(mode);
      applyToDom(resolved);
      set({ theme: mode, resolved });
    },
    apply() {
      const resolved = resolveTheme(get().theme);
      applyToDom(resolved);
      set({ resolved });
    },
    bootstrap() {
      const stored = readStoredTheme();
      const resolved = resolveTheme(stored);
      applyToDom(resolved);
      set({ theme: stored, resolved });

      // Re-apply when the OS theme flips, but only while the user is in
      // `'system'` mode — otherwise their explicit choice wins.
      if (
        !mediaListenerAttached &&
        typeof window !== 'undefined' &&
        window.matchMedia
      ) {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => {
          if (get().theme === 'system') get().apply();
        };
        // `addEventListener` is the modern API; older Safari only had `addListener`.
        if (typeof mq.addEventListener === 'function') {
          mq.addEventListener('change', onChange);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (mq as any).addListener(onChange);
        }
        /* AND AGAIN WHENEVER THE APP COMES BACK INTO VIEW. Browsers deliver
           the media-query `change` event during a RENDERING step, so a tab in
           the background, a minimised window or a WebView behind another app
           can miss the OS switching over — measured: an emulated switch on a
           hidden pane moved `matchMedia().matches` and delivered no event at
           all. The worker then returns at night to a page still in daylight
           colours while the setting says "system". Re-reading on return costs
           one media-query match and closes that gap. */
        if (typeof document !== 'undefined') {
          document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible' && get().theme === 'system') get().apply();
          });
        }
        mediaListenerAttached = true;
      }
    },
  }))
);
